import { Stack, Typography } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import dayjs from "dayjs";
import { TFunction } from "i18next";
import { formatNumber } from "../../../../../common/utils";
import { ConsumptionPageDTO, ConsumptionPageItemDTO } from "../../../../../datasources/api-client/data-contracts";

export const getNodeConsumptionsRows = (data: ConsumptionPageDTO, t: TFunction) => {
	if (!data?.items) return [];

	const safeNumber = (value: unknown): number => {
		const parsedValue = Number(value);

		return value === null || value === undefined || value === "" || Number.isNaN(parsedValue) ? 0 : parsedValue;
	};

	return data.items.map((item: ConsumptionPageItemDTO) => ({
		id: item.id,
		category: item.category || "-",
		period:
			item.startDate && item.endDate
				? `${dayjs(item.startDate).format("DD/MM/YYYY")} - ${dayjs(item.endDate).format("DD/MM/YYYY")}`
				: "-",
		detail: item.elementLabel || "-",
		consumption: safeNumber(item.consumption?.value),
		measure: item.consumption?.measure || "-",
		emissions: safeNumber(item.emissions?.value),
	}));
};

export const nodeConsumptionsCols: GridColDef[] = [
	{
		field: "id",
		headerName: "ID",
		flex: 1,
		sortable: false,
		renderCell: (params) => {
			return (
				<Stack height="100%" alignItems="start" justifyContent="center">
					<Typography variant="body2">{params.value}</Typography>
				</Stack>
			);
		},
	},
	{
		field: "category",
		headerName: "category",
		flex: 1,
		sortable: false,
		renderCell: (params) => {
			return (
				<Stack height="100%" alignItems="start" justifyContent="center">
					<Typography variant="body2">{params.value}</Typography>
				</Stack>
			);
		},
	},
	{
		field: "period",
		headerName: "period",
		flex: 1,
		sortable: false,
		renderCell: (params) => {
			return (
				<Stack height="100%" alignItems="start" justifyContent="center">
					<Typography variant="body2">{params.value}</Typography>
				</Stack>
			);
		},
	},
	{
		field: "detail",
		headerName: "detail",
		flex: 0.8,
		sortable: false,
		renderCell: (params) => {
			return (
				<Stack height="100%" alignItems="start" justifyContent="center">
					<Typography variant="body2">{params.value}</Typography>
				</Stack>
			);
		},
	},
	{
		field: "consumption",
		headerName: "consumption",
		flex: 0.8,
		sortable: false,
		renderCell: (params) => {
			return (
				<Stack height="100%" alignItems="flex-end" justifyContent="center" textAlign={"right"}>
					<Typography variant="body2">{formatNumber(params.value, "decimal", 2, 2)}</Typography>
				</Stack>
			);
		},
	},
	{
		field: "measure",
		headerName: "measure",
		flex: 0.5,
		sortable: false,
		renderCell: (params) => {
			return (
				<Stack height="100%" alignItems="start" justifyContent="center">
					<Typography variant="body2">{params.value}</Typography>
				</Stack>
			);
		},
	},
	{
		field: "emissions",
		headerName: "emissions",
		flex: 0.8,
		sortable: false,
		renderCell: (params) => {
			return (
				<Stack height="100%" alignItems="flex-end" justifyContent="center">
					<Typography variant="body2">{formatNumber(params.value, "decimal", 2, 2)}</Typography>
				</Stack>
			);
		},
	},
];

// Measure map for the columns
const UNIT_BY_FIELD: Record<string, string> = {
	emissions: "tCO2e",
};

export const getNodeConsumptionsColumns = (dataCol: GridColDef[], t: TFunction, headerTextColor: string) =>
	dataCol.map((col, index) => {
		const label = col.headerName ? t(`greenrouterList/${col.headerName}`) : "";

		// Return columns
		return {
			...col,
			headerName: label,
			renderHeader: () =>
				label && (
					<Stack direction="column" width="100%">
						<Stack
							justifyContent={index === 4 || index === 6 ? "end" : "start"}
							alignContent="start"
							flexDirection="row"
							alignItems="baseline"
						>
							<Typography fontWeight={700} variant="body2" sx={{ color: headerTextColor }}>
								{label}
							</Typography>
							<Typography fontWeight={700} sx={{ color: headerTextColor }} variant="caption" ml={0.5}>
								{UNIT_BY_FIELD[col.field]}
							</Typography>
						</Stack>
					</Stack>
				),
		};
	});

// Defines a single column in the Greenrouter table
export type GreenrouterColumn = {
	field: keyof GreenrouterRow; // Key in the row data
	label: string; // Header label
	sortable?: boolean; // Whether this column can be sorted
	hideable?: boolean; // Whether this column can be hidden
	visible: boolean; // If the column is currently visible
	width?: number; // Optional fixed width used for resizing
	flex?: number; // Optional flex value for automatic sizing
};

export type GreenrouterRow = {
	id: string;
	code: string;
	description?: string;
	typeDescription?: {
		typeId: number;
		typeDescription: string;
	};
	city?: string | null;
	country?: string | null;
	area?: string;
	emissions?: string;
};

// Generates the default columns for Greenrouter table
export const greenrouterColumns = (t: TFunction): GreenrouterColumn[] => [
	{
		field: "code",
		label: t("greenrouterList/operational unit"),
		sortable: true,
		hideable: true,
		visible: true,
		flex: 2,
	},
	{
		field: "description",
		label: t("greenrouterList/description"),
		sortable: true,
		hideable: true,
		visible: true,
		flex: 2,
	},
	{
		field: "typeDescription",
		label: t("greenrouterList/node type"),
		sortable: true,
		hideable: true,
		visible: true,
		flex: 2,
	},
	{
		field: "city",
		label: t("greenrouterList/city"),
		sortable: true,
		hideable: true,
		visible: true,
		flex: 1,
	},
	{
		field: "country",
		label: t("greenrouterList/country"),
		sortable: true,
		hideable: true,
		visible: true,
		flex: 1,
	},
	{
		field: "area",
		label: t("greenrouterList/area"),
		sortable: true,
		hideable: true,
		visible: true,
		flex: 1,
	},
	{
		field: "emissions",
		label: t("greenrouterList/emissions"),
		sortable: true,
		hideable: true,
		visible: true,
		flex: 1,
	},
];
